import { Label } from '@/components/ui/label';

// Format value as currency on blur
const formatCurrency = (value) => {
  if (!value || value === '') return '';

  // Remove any existing formatting ($ and commas)
  const numericValue = value.toString().replace(/[$,]/g, '');

  // Parse as float
  const number = parseFloat(numericValue);

  // If not a valid number, return empty
  if (isNaN(number)) return '';

  // Format as currency with $ sign, commas, and 2 decimal places
  return '$' + number.toLocaleString('en-NZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const handleFeeBlur = (fieldName, formData, setFormData) => {
  const formatted = formatCurrency(formData[fieldName]);
  setFormData({ ...formData, [fieldName]: formatted });
};

function CurrencyInput({ value, fieldName, formData, setFormData, readOnly, testId }) {
  return (
    <input
      type="text"
      className="w-full border rounded px-2 py-1 text-right"
      placeholder="$0.00"
      value={value}
      onChange={(e) => setFormData({ ...formData, [fieldName]: e.target.value })}
      onBlur={() => handleFeeBlur(fieldName, formData, setFormData)}
      disabled={readOnly}
      data-testid={testId}
    />
  );
}

function DisabledCell() {
  return (
    <input
      type="text"
      className="w-full border rounded px-2 py-1 text-right bg-gray-100 text-gray-400"
      placeholder="Not required"
      disabled
    />
  );
}

export function FeesSection({ formData, setFormData, readOnly }) {
  // Show/hide for the two team-related fee rows is gated by the
  // existing "Fees for Team Skaters?" dropdown (team_fee_type).
  //
  //   standard_individual  →  hide both team rows
  //   separate_individual  →  show Team Skater per-event fee,  hide flat Team Entry fee
  //   per_team             →  hide Team Skater per-event fee,  show flat Team Entry fee
  //   no_teams             →  hide both team rows
  const teamFeeType = formData.team_fee_type || 'standard_individual';
  const showTeamSkaterRow = teamFeeType === 'separate_individual';
  const showCompetitiveTeamRow = teamFeeType === 'per_team';

  return (
    <div className="p-4 border rounded-lg bg-green-50">
      <h4 className="font-medium mb-4 text-green-800">Registration & Fees</h4>

      {/* Fees for Team Skaters dropdown */}
      <div className="mb-4">
        <Label>Fees for Team Skaters?</Label>
        <select
          className="w-full border rounded-lg px-3 py-2 mt-1"
          value={teamFeeType}
          onChange={(e) => setFormData({ ...formData, team_fee_type: e.target.value })}
          disabled={readOnly}
          data-testid="competition-team-fee-type"
        >
          <option value="standard_individual">Standard Individual Skater Fee</option>
          <option value="separate_individual">Separate Individual fee if no individual entry</option>
          <option value="per_team">Entry fee per team, additional to any other fees</option>
          <option value="no_teams">There are NO TEAM EVENTS on offer</option>
        </select>
      </div>

      {/* Fee Structure Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="fees-table">
          <thead>
            <tr className="bg-green-100">
              <th className="border px-3 py-2 text-left w-48">Class of Skater</th>
              <th className="border px-3 py-2 text-center w-28">Base Fee</th>
              <th className="border px-3 py-2 text-center w-28">Add Per Event</th>
              <th className="border px-3 py-2 text-center w-28">Maximum</th>
            </tr>
          </thead>
          <tbody>
            {/* Row 1: Competitive Individual/Pairs */}
            <tr data-testid="fees-row-competitive">
              <td className="border px-3 py-2 bg-white">Competitive — Individual/Pairs</td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_competitive_base}
                  fieldName="fee_competitive_base"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-competitive-base"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_competitive_per_event}
                  fieldName="fee_competitive_per_event"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-competitive-per-event"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_competitive_max}
                  fieldName="fee_competitive_max"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-competitive-max"
                />
              </td>
            </tr>

            {/* Row 2: Team Skater (only when team_fee_type is "separate_individual") */}
            {showTeamSkaterRow && (
              <tr data-testid="fees-row-team-skater">
                <td className="border px-3 py-2 bg-white">Team Skater — no solo/pairs events</td>
                <td className="border px-1 py-1 bg-white">
                  <CurrencyInput
                    value={formData.fee_team_skater_base}
                    fieldName="fee_team_skater_base"
                    formData={formData}
                    setFormData={setFormData}
                    readOnly={readOnly}
                    testId="fee-team-skater-base"
                  />
                </td>
                <td className="border px-1 py-1 bg-white">
                  <CurrencyInput
                    value={formData.fee_team_skater_per_event}
                    fieldName="fee_team_skater_per_event"
                    formData={formData}
                    setFormData={setFormData}
                    readOnly={readOnly}
                    testId="fee-team-skater-per-event"
                  />
                </td>
                <td className="border px-1 py-1 bg-white">
                  <CurrencyInput
                    value={formData.fee_team_skater_max}
                    fieldName="fee_team_skater_max"
                    formData={formData}
                    setFormData={setFormData}
                    readOnly={readOnly}
                    testId="fee-team-skater-max"
                  />
                </td>
              </tr>
            )}

            {/* Row 3: Competitive Team (4+) flat team entry fee
                — only when team_fee_type is "per_team" */}
            {showCompetitiveTeamRow && (
              <tr data-testid="fees-row-team-entry">
                <td className="border px-3 py-2 bg-white">Competitive Team (4+) — team entry fee</td>
                <td className="border px-1 py-1 bg-white">
                  <CurrencyInput
                    value={formData.fee_team_base}
                    fieldName="fee_team_base"
                    formData={formData}
                    setFormData={setFormData}
                    readOnly={readOnly}
                    testId="fee-team-base"
                  />
                </td>
                <td className="border px-1 py-1 bg-gray-100"><DisabledCell /></td>
                <td className="border px-1 py-1 bg-gray-100"><DisabledCell /></td>
              </tr>
            )}

            {/* Row 4: Beginner Skaters */}
            <tr data-testid="fees-row-beginner">
              <td className="border px-3 py-2 bg-white">Beginner Skaters</td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_beginner_base}
                  fieldName="fee_beginner_base"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-beginner-base"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_beginner_per_event}
                  fieldName="fee_beginner_per_event"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-beginner-per-event"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_beginner_max}
                  fieldName="fee_beginner_max"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-beginner-max"
                />
              </td>
            </tr>

            {/* Row 5: Freeform / Other */}
            <tr data-testid="fees-row-other">
              <td className="border px-1 py-1 bg-white">
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  placeholder="Skater/Event Type eg Exhibition"
                  value={formData.fee_other_type}
                  onChange={(e) => setFormData({ ...formData, fee_other_type: e.target.value })}
                  disabled={readOnly}
                  data-testid="fee-other-type"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_other_base}
                  fieldName="fee_other_base"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-other-base"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_other_per_event}
                  fieldName="fee_other_per_event"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-other-per-event"
                />
              </td>
              <td className="border px-1 py-1 bg-white">
                <CurrencyInput
                  value={formData.fee_other_max}
                  fieldName="fee_other_max"
                  formData={formData}
                  setFormData={setFormData}
                  readOnly={readOnly}
                  testId="fee-other-max"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
